import { Component, inject, output, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialog, MatDialogRef, MAT_DIALOG_DATA  } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IntegrationError } from 'src/app/models/integration-error';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

interface DialogData {
  selectedError: IntegrationError;
  selectedStatus: CurrentStatus;
}

@Component({
  selector: 'app-lt-error-update-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './lt-error-update-dialog.html',
  styleUrl: './lt-error-update-dialog.scss',
})
export class LTErrorUpdateDialogComponent {
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<LTErrorUpdateDialogComponent>);

  errorUpdated = output<{
    error: IntegrationError;
    status: CurrentStatus;
    comment: string;
  }>();
  dialogCanceled = output<void>();


  // Inject dialog data instead of using input signals for dialog components
  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  // Convert injected data to signals for reactive behavior
  selectedError = signal(this.data?.selectedError || null);
  selectedStatus = signal(this.data?.selectedStatus || null);

  // internal signals
  private _resolutionComment = signal('');
  isUpdating = signal(false);

  // computed signals
  resolutionComment = computed(() => this._resolutionComment());

  canUpdate = computed(() => {
    const error = this.selectedError();
    const status = this.selectedStatus();
    return !!(error && status && !this.isUpdating());
  });

  constructor() {
    // initialize comment when selectedError changes
    effect(() => {
      const error = this.selectedError();
      if (error?.resolutionComment) {
        this._resolutionComment.set(error.resolutionComment);
      }
    });
  }

  updateComment(event: Event) {
    const target = event.target as HTMLInputElement;
    this._resolutionComment.set(target.value);
  }

  updateError(){
    const error = this.selectedError();
    const status = this.selectedStatus();
    
    if (!error || !status) {
      return;
    }

    this.isUpdating.set(true);

    // Emit the update event with all necessary data
    this.errorUpdated.emit({
      error,
      status,
      comment: this.resolutionComment(),
    });

    // Close dialog after a brief delay to show the updating state
    setTimeout(() => {
      this.isUpdating.set(false);
      this.closeDialog();
    }, 500);
  }

  cancelUpdate(){
    this.dialogCanceled.emit();
    this.closeDialog();
  }

  private closeDialog(){
    this.dialogRef?.close();
  }

  // Static method to open the dialog
  static open(
    dialog: MatDialog,
    selectedError: IntegrationError,
    selectedStatus: CurrentStatus,
  ): MatDialogRef<LTErrorUpdateDialogComponent> {
    const dialogRef = dialog.open(LTErrorUpdateDialogComponent, {
      width: '500px',
      disableClose: false,
      autoFocus: true,
      data: {
        selectedError,
        selectedStatus,
      } as DialogData,
    });

    return dialogRef;
  }

}
